import { useEffect, useState } from 'react';
import { api, ErreurApi, type FluxMetier } from './api';
import { Icone } from './icones';

/**
 * LE FLUX MÉTIER — où le travail s'accumule, et ce qui ne peut pas avancer.
 *
 * ⚠️ CET ÉCRAN RÉPOND À UNE QUESTION QUE NI LA FILE NI LE DOSSIER NE POSENT.
 * La file dit à un agent ce qui l'attend, LUI. Le dossier dit où en est UNE
 * affaire. Aucun des deux ne dit où le travail s'entasse — et c'est pourtant
 * la seule chose qu'un chef de service regarde le matin.
 *
 * ⚠️ UN REGISTRE VIDE LE DIT, IL N'AFFICHE PAS DES ZÉROS. Un tableau de bord
 * qui rend « 0 » partout sur un registre où rien n'est encore passé est
 * indiscernable d'un tableau de bord cassé — et c'est le plus mauvais des deux
 * messages, parce qu'il se lit comme une mesure.
 */

const LIBELLE_SERVICE: Record<string, string> = {
  administration: 'Direction du Cadastre',
  commune: 'Commune',
  conservation_fonciere: 'Conservation Foncière',
  etude_notariale: 'Étude notariale',
  cabinet_geometre: 'Cabinet de géomètre',
  service_fiscal: 'Service fiscal',
  collectivite: 'Collectivité',
  autre: 'Autre service',
};
const svc = (t: string) => LIBELLE_SERVICE[t] ?? t;

export function Flux() {
  const [f, setF] = useState<FluxMetier | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    api.demandes.flux().then(setF).catch((e) =>
      setErreur(e instanceof ErreurApi && e.estRefus
        ? "Votre rôle ne permet pas de consulter le flux (fon.demande:lire)."
        : String((e as Error).message ?? e)));
  }, []);

  if (erreur) return <p className="erreur" role="alert">{erreur}</p>;
  if (!f) return <div className="attente-plein">Chargement du flux…</div>;

  const vierge = f.population.dossiers_total === 0;

  return (
    <div className="tableau-bord">
      <header className="entete-scene">
        <h2>Flux métier</h2>
        <div className="actions">
          <span className="jeton"><b>{f.population.dossiers_vivants}</b> dossier(s) en cours</span>
          <span className="jeton"><b>{f.population.demandes_vivantes}</b> demande(s) en circulation</span>
          <span className="jeton"><b>{f.population.titres_delivres}</b> titre(s) délivré(s)</span>
        </div>
      </header>

      {/* ⚠️ LA DISTINCTION QUI COMPTE : rien n'attend, ou rien n'est jamais
          passé. Les deux donnent les mêmes zéros. */}
      {vierge && (
        <div className="avertissement">
          <strong>Aucun dossier n'a encore été ouvert.</strong> Les compteurs ci-dessous
          sont à zéro parce que le registre n'a rien reçu — non parce que le travail
          serait à jour. Déposez une demande au Guichet pour amorcer un parcours.
        </div>
      )}

      {/* ── LE PARCOURS D'UNE AFFAIRE ─────────────────────────────────────── */}
      <section>
        <h4>Le parcours d'une affaire, de la demande au titre remis</h4>
        <ol className="parcours-metier">
          {f.parcours.map((e, i) => (
            <li key={e.etape} className={e.affaires > 0 ? 'peuplee' : 'vide'}>
              <div className="pm-rang">{i + 1}</div>
              <div className="pm-corps">
                <div className="pm-tete">
                  <strong>{e.libelle}</strong>
                  <span className="pm-qui">{e.qui}</span>
                  {e.affaires > 0 && (
                    <span className="etat bleu">{e.affaires} dossier{e.affaires > 1 ? 's' : ''}</span>
                  )}
                  {/* Le plus ancien dit si ça bouge, ce qu'un total ne dit pas. */}
                  {e.plusAncienJours !== null && e.plusAncienJours > 0 && (
                    <span className="discret">le plus ancien : {e.plusAncienJours} j</span>
                  )}
                </div>
                <p className="pm-quoi">{e.quoi}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── LES CIRCUITS ──────────────────────────────────────────────────── */}
      <section>
        <h4>Les circuits de demande</h4>
        <p className="cadre-aide">
          Une demande validée est posée chez le premier service de son circuit, puis passe
          au suivant dès que celui-ci a terminé. Elle ne saute jamais un poste.
        </p>
        {f.circuits.map((c) => (
          <div key={c.code} className="circuit-bloc">
            <div className="cb-tete">
              <strong>{c.libelle}</strong>
              {c.delaiJours && <span className="jeton">délai {c.delaiJours} j</span>}
              {c.produit && <span className="discret">produit : {c.produit}</span>}
            </div>
            <div className="cb-postes">
              {c.postes.map((p, i) => (
                <div key={p.service}
                     className={`cb-poste ${p.chezMoi ? 'mien' : ''} ${p.enRetard > 0 ? 'retard' : ''}`}>
                  {i > 0 && <Icone nom="chevron" taille={12} className="cb-fleche" />}
                  <div className="cb-carte">
                    <span className="cb-svc">{svc(p.service)}</span>
                    <span className="cb-n">{p.enAttente}</span>
                    {p.enRetard > 0 && (
                      <span className="cb-retard">
                        <Icone nom="alerte" taille={11} /> {p.enRetard} hors délai
                      </span>
                    )}
                    {p.enAttente > 0 && p.plusAncienJours !== null && (
                      <span className="cb-age">plus ancien : {p.plusAncienJours} j</span>
                    )}
                    {p.chezMoi && <span className="cb-mien">votre service</span>}
                  </div>
                </div>
              ))}
            </div>
            <div className="cb-canaux">
              Déposable par : {c.canaux.map(svc).join(' · ')}
            </div>
          </div>
        ))}
      </section>

      {/* ── CE QUI NE PEUT PAS AVANCER ────────────────────────────────────── */}
      <section>
        <h4>Ce qui ne peut pas avancer</h4>
        {f.bloques.length === 0 ? (
          <p className="vide">
            {f.population.dossiers_total === 0
              ? "Aucun dossier en cours : rien ne peut être bloqué."
              : "Aucun dossier n'attend un avis impossible à obtenir."}
          </p>
        ) : (
          <table className="tableau large">
            <thead><tr><th>Dossier</th><th>Nature</th><th>Avis attendu</th><th>État</th><th>Remède</th></tr></thead>
            <tbody>
              {f.bloques.map((b) => (
                <tr key={`${b.reference}-${b.avis_attendu}`} className={b.inatteignable ? 'ligne-alerte' : undefined}>
                  <td><code>{b.reference}</code></td>
                  <td>{b.type}</td>
                  <td>{svc(b.avis_attendu)}</td>
                  <td>
                    {/* ⚠️ « LENT » ET « MORT » NE SE TRAITENT PAS PAREIL. Un
                        dossier dont l'avis est inatteignable ne repartira pas
                        si on relance l'agent : il faut en habiliter un autre. */}
                    {b.inatteignable
                      ? <span className="etat rouge">inatteignable</span>
                      : <span className="etat ambre">en attente · {b.donneurs_possibles} agent(s) peuvent</span>}
                  </td>
                  <td className="discret">{b.remede ?? 'Relancer le service concerné.'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}
